import React from 'react';
import { ArrowRight, Code2, Plus } from 'lucide-react';
import { TableColumn } from '../../types/domain';
import { Button } from '../ui/Button';

interface EmptyHedgehogProps {
  type?: 'no-submissions' | 'no-tables' | 'no-filter-results';
  projectSlug?: string;
  tableSlug?: string;
  ingestToken?: string;
  columns?: TableColumn[];
  onAction?: () => void;
  onNavigateEndpoints?: () => void;
}

export const EmptyHedgehog: React.FC<EmptyHedgehogProps> = ({
  type = 'no-submissions',
  onAction,
  onNavigateEndpoints,
}) => {
  return (
    <div className="flex flex-col items-center justify-center p-8 bg-cf-surface border border-cf-border rounded-lg max-w-lg mx-auto my-8 text-center animate-in fade-in-50 duration-200">
      {/* Mascot Graphic: Resting Hedgehog waiting for data */}
      <svg width="56" height="56" viewBox="0 0 64 64" fill="none" className="mb-4">
        <ellipse cx="32" cy="38" rx="24" ry="18" className="fill-cf-surfaceActive stroke-cf-border" strokeWidth="2" />
        <path
          d="M 12 36 L 8 26 L 16 30 L 15 18 L 24 24 L 26 12 L 34 22 L 38 14 L 42 26 L 48 20 L 46 32 Z"
          className="fill-cf-textPrimary"
          opacity="0.85"
        />
        <path d="M 46 36 L 54 40 L 48 44 Z" className="fill-cf-textSecondary" />
        <circle cx="53" cy="40" r="1.5" className="fill-cf-textPrimary" />
        <path d="M 44 38 Q 42 41 40 38" className="stroke-cf-textPrimary" strokeWidth="1.5" strokeLinecap="round" />
        <text x="46" y="20" className="fill-cf-textSecondary" fontSize="12" fontFamily="JetBrains Mono" fontWeight="bold">z</text>
        <text x="52" y="14" className="fill-cf-textMuted" fontSize="14" fontFamily="JetBrains Mono" fontWeight="bold">z</text>
        <text x="58" y="8" className="fill-cf-textMuted" fontSize="16" fontFamily="JetBrains Mono" fontWeight="bold">{'}'}</text>
      </svg>

      <h3 className="text-sm font-semibold text-cf-textPrimary mb-1">
        {type === 'no-submissions'
          ? 'No Submissions Captured Yet'
          : type === 'no-tables'
          ? 'No Datastores in Project'
          : 'Zero Filter Matches'}
      </h3>

      <p className="text-xs text-cf-textSecondary max-w-sm mb-5 leading-relaxed">
        {type === 'no-submissions'
          ? 'This datastore has not received any records yet. Connect your HTML form or send API webhooks to start ingesting submissions.'
          : type === 'no-tables'
          ? 'Create your first dynamic table to start capturing structured data.'
          : 'No rows match the specified column filters. Try adjusting your query.'}
      </p>

      {type === 'no-submissions' && (
        <div className="flex items-center gap-2">
          {onNavigateEndpoints && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onNavigateEndpoints}
              leftIcon={<Code2 size={13} />}
              rightIcon={<ArrowRight size={12} />}
            >
              Endpoints &amp; Ingress
            </Button>
          )}
          {onAction && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onAction}
              leftIcon={<Plus size={13} />}
            >
              Add Row Manually
            </Button>
          )}
        </div>
      )}

      {type === 'no-tables' && onAction && (
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={onAction}
          leftIcon={<Plus size={14} />}
        >
          Create First Table
        </Button>
      )}
    </div>
  );
};
